import React, { useState } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import ChatArea from './components/ChatArea';
import CreateGroupModal from './components/CreateGroupModal';
import GroupInfoModal from './components/GroupInfoModal';
import AuthModal from './components/Auth/AuthModal';
import SettingsModal from './components/Settings/SettingsModal';
import { mockPrivateChats, mockGroups } from './data/mockData';
// import ChatService from './services/chatService'

const AppContent = () => {
    const { isDark, toggleTheme } = useTheme();
    const { user, isAuthenticated, loading, login, register, logout, updateServerUrl } = useAuth();

    const [privateChats, setPrivateChats] = useState(mockPrivateChats);
    // const [privateChats, setPrivateChats] = useState(ChatService.ChatsList);
    const [groups, setGroups] = useState(mockGroups);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [currentChatType, setCurrentChatType] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isGroupInfoOpen, setIsGroupInfoOpen] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(!isAuthenticated);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [authError, setAuthError] = useState(null);

    const currentUser = user?.displayName || user?.username || "Вы";

    // Показываем модальное окно аутентификации если пользователь не авторизован
    React.useEffect(() => {
        if (!loading && !isAuthenticated) {
            setIsAuthModalOpen(true);
        }
    }, [loading, isAuthenticated]);

    const handleLogin = async (credentials) => {
        const result = await login(credentials);
        if (result.success) {
            setIsAuthModalOpen(false);
            setAuthError(null);
        } else {
            setAuthError(result.error);
        }
    };

    const handleRegister = async (userData) => {
        const result = await register(userData);
        if (result.success) {
            setIsAuthModalOpen(false);
            setAuthError(null);
        } else {
            setAuthError(result.error);
        }
    };

    const handleLogout = async () => {
        await logout();
        setIsAuthModalOpen(true);
        setIsSettingsOpen(false);
        setCurrentChatId(null);
        setCurrentChatType(null);
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'var(--bg-primary)'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: 'var(--primary)' }}></i>
                    <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Загрузка...</p>
                </div>
            </div>
        );
    }

    const currentChat = currentChatType === 'private'
        ? privateChats.find(c => c.id === currentChatId)
        : groups.find(g => g.id === currentChatId);

    const handleChatSelect = (chatId, type) => {
        setCurrentChatId(chatId);
        setCurrentChatType(type);

        if (type === 'private') {
            setPrivateChats(prev => prev.map(chat =>
                chat.id === chatId ? { ...chat, unread: 0 } : chat
            ));
        } else {
            setGroups(prev => prev.map(group =>
                group.id === chatId ? { ...group, unread: 0 } : group
            ));
        }
    };

    const handleSendMessage = (text) => {
        if (!currentChatId) return;

        const newMessage = {
            id: Date.now(),
            text: text,
            time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
            sender: currentUser,
            type: 'outgoing'
        };

        if (currentChatType === 'private') {
            setPrivateChats(prev => prev.map(chat => {
                if (chat.id === currentChatId) {
                    const updatedChat = {
                        ...chat,
                        messages: [...chat.messages, newMessage],
                        lastMessage: text,
                        time: newMessage.time
                    };
                    setTimeout(() => simulateReply(updatedChat, 'private'), 1500);
                    return updatedChat;
                }
                return chat;
            }));
        } else {
            setGroups(prev => prev.map(group => {
                if (group.id === currentChatId) {
                    const updatedGroup = {
                        ...group,
                        messages: [...group.messages, newMessage],
                        lastMessage: text,
                        time: newMessage.time
                    };
                    setTimeout(() => simulateReply(updatedGroup, 'group'), 1500);
                    return updatedGroup;
                }
                return group;
            }));
        }
    };

    const simulateReply = (chat, type) => {
        const replies = ["Понял, спасибо!", "Хорошо, договорились", "Отлично!", "Скоро отвечу", "👍", "Понял, принято"];
        const randomReply = replies[Math.floor(Math.random() * replies.length)];

        let randomSender = chat.name;
        if (type === 'group' && chat.members) {
            const otherMembers = chat.members.filter(m => m !== currentUser);
            randomSender = otherMembers[Math.floor(Math.random() * otherMembers.length)] || chat.members[0];
        }

        const replyMessage = {
            id: Date.now(),
            text: randomReply,
            time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
            sender: randomSender,
            type: 'incoming'
        };

        if (type === 'private') {
            setPrivateChats(prev => prev.map(c => {
                if (c.id === chat.id) {
                    const updatedChat = {
                        ...c,
                        messages: [...c.messages, replyMessage],
                        lastMessage: randomReply,
                        time: replyMessage.time
                    };
                    if (currentChatId !== chat.id || currentChatType !== 'private') {
                        updatedChat.unread = (updatedChat.unread || 0) + 1;
                    }
                    return updatedChat;
                }
                return c;
            }));
        } else {
            setGroups(prev => prev.map(g => {
                if (g.id === chat.id) {
                    const updatedGroup = {
                        ...g,
                        messages: [...g.messages, replyMessage],
                        lastMessage: randomReply,
                        time: replyMessage.time
                    };
                    if (currentChatId !== chat.id || currentChatType !== 'group') {
                        updatedGroup.unread = (updatedGroup.unread || 0) + 1;
                    }
                    return updatedGroup;
                }
                return g;
            }));
        }
    };

    const handleCreateGroup = (name, members) => {
        const newGroup = {
            id: groups.length + 1,
            name: name,
            avatar: name.charAt(0),
            members: [...members, currentUser],
            lastMessage: "Группа создана",
            time: "Только что",
            unread: 0,
            messages: [{
                id: 1,
                text: `Группа "${name}" создана`,
                time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
                sender: "Система",
                type: 'incoming'
            }]
        };

        setGroups(prev => [...prev, newGroup]);
        handleChatSelect(newGroup.id, 'group');
    };

    const handleLeaveGroup = () => {
        if (!currentChatId || currentChatType !== 'group') return;

        if (window.confirm("Вы уверены, что хотите покинуть группу?")) {
            setGroups(prev => prev.filter(g => g.id !== currentChatId));
            setCurrentChatId(null);
            setCurrentChatType(null);
            setIsGroupInfoOpen(false);
        }
    };

    return (
        <>
            <div className="messenger-container">
                <Sidebar
                    privateChats={privateChats}
                    groups={groups}
                    currentChatId={currentChatId}
                    currentChatType={currentChatType}
                    onChatSelect={handleChatSelect}
                    onCreateGroup={() => setIsCreateModalOpen(true)}
                    onOpenSettings={() => setIsSettingsOpen(true)}
                    user={user}
                />
                <ChatArea
                    currentChat={currentChat}
                    currentChatType={currentChatType}
                    onSendMessage={handleSendMessage}
                    onShowGroupInfo={() => setIsGroupInfoOpen(true)}
                    onToggleTheme={toggleTheme}
                    isDark={isDark}
                />
            </div>

            <AuthModal
                isOpen={isAuthModalOpen}
                onClose={() => {
                    if (!isAuthenticated) return;
                    setIsAuthModalOpen(false);
                }}
                onLogin={handleLogin}
                onRegister={handleRegister}
                error={authError}
            />

            <CreateGroupModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreateGroup={handleCreateGroup}
            />

            <GroupInfoModal
                isOpen={isGroupInfoOpen}
                group={currentChatType === 'group' ? currentChat : null}
                onClose={() => setIsGroupInfoOpen(false)}
                onLeaveGroup={handleLeaveGroup}
                currentUser={currentUser}
            />

            <SettingsModal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                user={user}
                onLogout={handleLogout}
                onUpdateServerUrl={updateServerUrl}
            />
        </>
    );
};

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
